import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

const STATUS = { Pending: "In attesa di approvazione", Approved: "Approvato dall'amministratore", Rejected: "Rifiutato" };
const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ConfirmDay = ({ date, stationId, summary, isAdmin, isSupervisor, onDone }) => {
  const [info, setInfo] = useState(null);
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const key = `${summary.km}-${summary.fuel_total}-${summary.vehicles}`;
  useEffect(() => {
    api.get("/assignments/confirm-info", { params: { date, station_id: stationId } }).then((r) => {
      setInfo(r.data);
      setPrice((p) => p || (r.data.confirmed?.price ?? r.data.last_price ?? "").toString());
    });
  }, [date, stationId, key]);
  if (!info || !info.can_confirm || !summary.count) return null;

  const c = info.confirmed;
  const changed = c && (c.km !== summary.km || c.fuel !== summary.fuel_total || c.vehicles !== summary.vehicles);
  const approved = info.entry?.status === "Approved";
  const locked = approved && !isAdmin;
  const confirm = async () => {
    const msg = approved ? "Il record di questa data è già approvato: verrà aggiornato con i nuovi dati e tornerà in attesa di approvazione. Continuare?" : info.entry ? "Esiste già un record nei Dati stazione per questa data: verrà aggiornato e tornerà in attesa di approvazione. Continuare?" : "Inviare la giornata ai Dati stazione per l'approvazione?";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.post("/assignments/confirm", { date, station_id: stationId, fuel_price_per_liter: Number(price) });
      toast.success("Giornata inviata ai Dati stazione");
      setInfo(null);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-950" data-testid="confirm-day-card">
      <div className="flex flex-wrap items-start gap-3">
        {c && !changed ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" /> : <Send className="mt-0.5 h-5 w-5 shrink-0" />}
        <div className="min-w-[14rem] flex-1 space-y-1">
          <p className="font-semibold">Conferma giornata → Dati stazione</p>
          <p>{summary.vehicles} furgoni · <b>{summary.km.toLocaleString("it-IT")} km</b> · gasolio <b>{eur(summary.fuel_total)}</b>{summary.extra_fuel > 0 && <span className="text-xs"> (di cui extra {eur(summary.extra_fuel)})</span>}{summary.missing > 0 && <span className="text-rose-700"> · {summary.missing} con km di default</span>}</p>
          {c && <p className="text-xs" data-testid="confirm-day-status">Confermata da {c.confirmed_by} il {new Date(`${c.confirmed_at}Z`).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}{info.entry && ` · ${STATUS[info.entry.status]}`}</p>}
          {changed && <p className="flex items-center gap-1 text-xs font-semibold text-amber-700" data-testid="confirm-day-changed"><AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Le assegnazioni sono cambiate dopo la conferma ({c.km.toLocaleString("it-IT")} km, {eur(c.fuel)} inviati).{locked ? " Il record è già approvato: chiedi all'amministratore di aggiornarlo." : " Conferma di nuovo per aggiornare i Dati stazione."}</p>}
          {!c && info.entry && !approved && <p className="text-xs text-amber-700">C'è già un record per questa data ({info.entry.kilometers} km, {eur(info.entry.total_amount)}): verrà aggiornato.</p>}
          {approved && !changed && <p className="text-xs font-semibold text-emerald-700" data-testid="confirm-day-approved">Il record di questa data è già approvato.</p>}
        </div>
        {!locked && !isSupervisor && (
          <div className="flex items-end gap-2">
            <label className="space-y-1"><span className="block text-xs">Prezzo gasolio €/l</span>
              <Input type="number" step="0.001" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className="w-28 bg-white" data-testid="confirm-day-price" /></label>
            <Button onClick={confirm} disabled={busy || !(Number(price) > 0) || !(summary.fuel_total > 0)} data-testid="confirm-day-button">{c ? "Conferma di nuovo" : "Conferma e invia"}</Button>
          </div>
        )}
      </div>
      {!(summary.fuel_total > 0) && !locked && !isSupervisor && <p className="mt-2 text-xs text-rose-700">Inserisci il gasolio dei furgoni per poter confermare.</p>}
    </div>
  );
};
